import { Navigate, Outlet, useLocation } from '@tanstack/react-router';
import { useMe, useRefreshMe, useTwoFactorActions } from '@blimo/api-client/react';
import { getErrorMessage } from '@blimo/api-client';
import { Alert, AlertDescription, Button } from '@blimo/ui';
import { FullPageSpinner, NoAccessPage, TwoFactorRequiredPage } from '@blimo/ui/auth';
import { AppShell } from '../layout/app-shell.jsx';
import { useLogout } from '../lib/use-logout.js';
import { env } from '../lib/api.js';
import { useAppName } from '../lib/app-name.js';

/**
 * Guards every page behind login. The route's beforeLoad already redirected
 * anonymous users; this component reacts to later changes of /me.
 */
export function ProtectedLayout() {
  const appName = useAppName();
  const { data: me, error, isPending, refetch } = useMe();
  const location = useLocation();
  const logout = useLogout();
  const twoFactorActions = useTwoFactorActions();
  const refreshMe = useRefreshMe();

  if (isPending) return <FullPageSpinner />;

  if (error) {
    if (error.status === 401) return <Navigate to="/login" search={{ redirect: location.href }} replace />;
    if (error.isTwoFactorRequired) return <TwoFactorRequiredPage appName={appName} actions={twoFactorActions} onDone={refreshMe} onLogout={logout} />;
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 p-6">
        <Alert variant="destructive">
          <AlertDescription>{getErrorMessage(error, 'Die Anwendung konnte nicht geladen werden.')}</AlertDescription>
        </Alert>
        <Button onClick={() => refetch()}>Erneut versuchen</Button>
      </div>
    );
  }

  if (me.two_factor?.required && !me.two_factor?.enabled) {
    return <TwoFactorRequiredPage appName={appName} actions={twoFactorActions} onDone={refreshMe} onLogout={logout} />;
  }

  if (!me.apps?.portal) {
    return (
      <NoAccessPage appName={appName} name={me.name} otherApp={me.apps?.admin ? { label: 'Zur Verwaltung', href: env.adminUrl } : null} onLogout={logout} />
    );
  }

  return (
    <AppShell me={me}>
      <Outlet />
    </AppShell>
  );
}
